import { apiConfig } from "../config/apiConfig";
import { apiCaching } from "../config/cachingConfig";
import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { checkIncognito } from "../services/worker-injector";
import { createVDOMHash } from "../util/keyGeneration";
import { VNode } from "../util/virtualDom";
import { getImageReplacemnt } from "./imageHelper";
import { insertTemplate, lookupTemplate } from "../services/woker-api";

export const TESTIMONIAL_SELECTOR = ".testimonials-section";

// Constants to avoid magic numbers
const STAR_MAX = 5;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const PROFILE_WIDTH = 72;
const PROFILE_HEIGHT = 73;

// Reusable type alias for identifier-like values used across methods
type IdLike = string | number | null;

// Strong type for testimonial input shape
export interface TestimonialData {
  reviewAuthorName: string;
  reviewPhotoUrl: string | null;
  reviewStarRating?: number;
  city?: string;
  reviewSourceType?: string;
  reviewBody?: string;
  reviewResponseBody?: string;
}

export class TestimonialCommon {
  private static instance: TestimonialCommon | null = null;

  static getInstance(): TestimonialCommon {
    TestimonialCommon.instance??= new TestimonialCommon();
    return TestimonialCommon.instance;
  }

  private constructor() {}

  buildUrl(
    conceptId: IdLike,
    weblocationId: IdLike,
    countryId: IdLike,
    pageNumber: number,
    pageSize: number,
    rating: number
  ): string {
    // Coerce potentially null identifiers to sensible defaults
    const concept = conceptId ?? 0;
    const wl = weblocationId ?? 0;
    const country = countryId ?? 0;
    return `${apiConfig.TESTIMONIAL_REVIEWS}/${concept}/${wl}/${country}/0/${pageNumber}/${pageSize}/${rating}`;
  }

  async getDataFromServer(
    conceptId: IdLike,
    weblocationId: IdLike,
    countryId: IdLike,
    pageNumber: number,
    pageSize: number,
    rating: number
  ) {
    const url = this.buildUrl(
      conceptId,
      weblocationId,
      countryId,
      pageNumber,
      pageSize,
      rating
    );
    return cachedAPIWrapper(url, apiCaching.TestimonyAndReview);
  }

  private clampRating(r: number): number {
    if (Number.isNaN(r)) return 0;
    return Math.max(0, Math.min(STAR_MAX, r));
  }

  private computeStarBuckets(rating: number): { full: number; half: boolean; empty: number } {
    const r = this.clampRating(rating);
    const full = Math.floor(r);
    const half = r - full >= 0.5 && full < STAR_MAX;
    const empty = STAR_MAX - full - (half ? 1 : 0);
    return { full, half, empty };
  }

  generateRatingStars(rating: number): string {
    const { full, half, empty } = this.computeStarBuckets(rating);
    const fullStar = '<span class="star full">★</span>';
    const halfStar = '<span class="star half">★</span>';
    const emptyStar = '<span class="star empty">☆</span>';
    return fullStar.repeat(full) + (half ? halfStar : '') + emptyStar.repeat(empty);
  }

  createTestimonialVNode(data: TestimonialData): VNode | null {
    if (!data?.reviewAuthorName) {
      return null;
    }
    const fullName: string = data.reviewAuthorName;
    // Prefer Unicode-aware tokenization; gracefully fallback
    const unicodeTokens = fullName.match(/\p{L}+/gu) || fullName.match(/\b[a-zA-Z]+\b/g) || [];
    let displayName = fullName;
    if (unicodeTokens.length === 1) {
      displayName = unicodeTokens[0];
    } else if (unicodeTokens.length >= 2) {
      displayName = `${unicodeTokens[0]} ${unicodeTokens[1].charAt(0).toUpperCase()}.`;
    }

    const photoDiv =
      data.reviewPhotoUrl === null
        ? getImageReplacemnt(displayName, "testimonialVdom")
        : {
            type: "div",
            props: { class: "testi-profile" },
            children: [
              {
                type: "img",
                props: {
                  src: data.reviewPhotoUrl,
                  alt: `Profile image for ${displayName}`,
                  width: PROFILE_WIDTH,
                  height: PROFILE_HEIGHT,
                },
                children: [],
              },
            ],
          };

    const ratingStars: VNode[] = [];
    const buckets = this.computeStarBuckets(data.reviewStarRating ? data.reviewStarRating : 0);
    for (let i = 0; i < buckets.full; i++) {
      ratingStars.push({ type: "span", props: { class: "star full" }, children: ["★"] });
    }
    if (buckets.half) {
      ratingStars.push({ type: "span", props: { class: "star half" }, children: ["★"] });
    }
    for (let i = 0; i < buckets.empty; i++) {
      ratingStars.push({ type: "span", props: { class: "star" }, children: ["☆"] });
    }

    return {
      type: "li",
      props: { class: "testi-main-wrap" },
      children: [
        {
          type: "div",
          props: { class: "testi-main" },
          children: [
            {
              type: "div",
              props: { class: "testi-details" },
              children: [
                photoDiv,
                {
                  type: "div",
                  props: { class: "testi-bio" },
                  children: (() => {
                    const arr: (VNode | string)[] = [];
                    arr.push({ type: "strong", props: { class: "user-name" }, children: [displayName] });
                    if (data.city) {
                      arr.push({ type: "address", props: { class: "address" }, children: [data.city] });
                    }
                    arr.push({ type: "div", props: { class: "rating" }, children: ratingStars });
                    if (data.reviewSourceType) {
                      arr.push({ type: "span", props: { class: "review-source" }, children: [`via ${data.reviewSourceType}`] });
                    }
                    return arr;
                  })(),
                },
              ],
            },
            {
              type: "div",
              props: { class: "testi-comment" },
              children: (() => {
                const arr: (VNode | string)[] = [];
                if (data.reviewBody) {
                  arr.push({ type: "p", props: { class: "body-text-sm" }, children: ["“" + data.reviewBody + "”"] });
                }
                if (data.reviewResponseBody) {
                  arr.push({
                    type: "div",
                    props: { class: "testi-reply" },
                    children: [
                      { type: "p", props: { class: "body-text-sm" }, children: ["Owner Response:"] },
                      { type: "p", props: { class: "body-text-sm" }, children: ["“" + data.reviewResponseBody + "”"] },
                    ],
                  });
                }
                return arr;
              })(),
            },
          ],
        },
      ],
    };
  }

  async storeInCache(key: string, data: TestimonialData[]): Promise<void> {
    try {
      const vdomNodes = data
        .map((item: TestimonialData) => this.createTestimonialVNode(item))
        .filter((node: VNode | null): node is VNode => node !== null);
      const hash = await createVDOMHash(vdomNodes);
      await insertTemplate(key, {
        responseHash: hash,
        generatedTemplate: JSON.stringify(vdomNodes),
        expiryTimestamp: Date.now() + CACHE_TTL_MS,
      });
    } catch (error) {
      console.warn("Error storing in cache:", error);
    }
  }

  async getFromCache(
    key: string,
    selectedStarRating: number,
    ratingCache: { [key: number]: any }
  ): Promise<any> {
    try {
      const cached = await lookupTemplate(key);
      if (!cached?.generatedTemplate) return null;
      const parsed = JSON.parse(cached.generatedTemplate);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch (error) {
      console.warn("Error retrieving from cache:", error);
      this.handleCacheFailure(ratingCache);
      return ratingCache[selectedStarRating] ?? null;
    }
  }

  handleCacheFailure(ratingCache: { [key: number]: any }): void {
    localStorage.setItem("testimonialCacheError", "true");
    // eslint-disable-next-line no-param-reassign
    for (const key of Object.keys(ratingCache)) {
      delete ratingCache[Number(key)];
    }
  }

  async createTestimonialLists(result: any[]): Promise<VNode[]> {
    const isIncognito = await checkIncognito();
    if (isIncognito) {
      return result
        .map((item) => this.createTestimonialVNode(item) ?? null)
        .filter((item): item is VNode => item !== null);
    }
    return Array.isArray(result) && result[0]?.type
      ? (result as VNode[])
      : result
          .map((item) => this.createTestimonialVNode(item))
          .filter((node): node is VNode => node !== null);
  }
}

export default TestimonialCommon;
